<template>
  <section class="w-full py-16 md:py-20 lg:py-24 bg-white">
    <div class="max-w-5xl mx-auto px-6 md:px-8 lg:px-12">
      <!-- Header Section -->
      <div class="text-center mb-12 md:mb-16">
        <!-- Title with blue dot -->
        <div class="flex items-center justify-center gap-3 mb-4">
          <div class="w-5 h-5 rounded-full bg-[#0A2F70]"></div>
          <!-- Titre - Mobile -->
          <H3Title className="md:hidden text-2xl sm:text-3xl font-extrabold text-[#0F7F5C] tracking-tight">
            Nous répondons à vos questions
          </H3Title>
          
          <!-- Titre - Desktop -->
          <H2Title className="hidden md:block text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0F7F5C] tracking-tight">
            Nous répondons à vos questions
          </H2Title>
        </div>
        
        <!-- Subtitle - Mobile -->
        <PContent className="md:hidden text-sm sm:text-base text-gray-600 font-normal max-w-2xl mx-auto">
          Trouvez des réponses aux questions courantes sur la plateforme de signalement
        </PContent>
        
        <!-- Subtitle - Desktop -->
        <H4Title className="hidden md:block text-base md:text-lg text-gray-600 font-normal max-w-2xl mx-auto">
          Trouvez des réponses aux questions courantes sur la plateforme de signalement
        </H4Title>
      </div>

      <!-- FAQ Accordion -->
      <transition-group
        name="fade"
        tag="div"
        class="space-y-0"
      >
        <div 
          v-for="(faq, index) in displayedFaqs" 
          :key="faq.id"
          class="border-b border-gray-200"
        >
          <button
            @click="toggleItem(index)"
            class="w-full py-5 md:py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
          >
            <PContent 
              :class="[
                'text-base md:text-lg pr-4',
                openItems[index]
                  ? 'font-semibold text-[#0D47A1]' 
                  : 'font-medium text-gray-800'
              ]"
            >
              {{ faq.question }}
            </PContent>
            
            <!-- Icon with rotation animation -->
            <div class="shrink-0">
              <div 
                :class="[
                  'transition-transform duration-300 ease-in-out',
                  openItems[index] ? 'rotate-180' : 'rotate-0'
                ]"
              >
                <Plus 
                  :class="[
                    'w-5 h-5',
                    openItems[index] ? 'text-[#0D47A1]' : 'text-gray-600'
                  ]" 
                />
              </div>
            </div>
          </button>
          
          <!-- Animated content -->
          <transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 max-h-0"
            enter-to-class="opacity-100 max-h-96"
            leave-active-class="transition-all duration-300 ease-in"
            leave-from-class="opacity-100 max-h-96"
            leave-to-class="opacity-0 max-h-0"
          >
            <div
              v-show="openItems[index]"
              class="overflow-hidden"
            >
              <div class="pb-5 md:pb-6 pr-12">
                <PContent class="text-gray-600 text-justify">
                  {{ faq.answer }}
                </PContent>
              </div>
            </div>
          </transition>
        </div>
      </transition-group>

      <!-- Load More Button -->
      <div 
        v-if="hasMoreFaqs"
        class="mt-8 md:mt-12 flex justify-center"
      >
        <button
          @click="loadMoreFaqs"
          :disabled="isLoading"
          class="group relative px-6 md:px-8 py-3 md:py-4 bg-[#0A2F70] text-white font-semibold text-base md:text-lg rounded-lg hover:bg-[#0D47A1] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
        >
          <span v-if="!isLoading" class="flex items-center gap-2">
            Voir autres questions
            <svg 
              class="w-5 h-5 transition-transform duration-300 group-hover:translate-y-1" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </span>
          <span v-else class="flex items-center gap-2">
            <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Chargement...
          </span>
        </button>
      </div>

      <!-- No More FAQs Message -->
      <div 
        v-else
        class="mt-8 text-center"
      >
        <PContent class="text-gray-500 italic">
          Vous avez consulté toutes les questions disponibles Merci.
        </PContent>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { H2Title, H3Title, H4Title, PContent } from '@/components/atoms/text'
import { Plus } from 'lucide-vue-next'
import question_responses from './question'


defineOptions({
  name: 'FaqSection'
})

// FAQ data structure
interface FaqItem {
  id: number
  question: string
  answer: string
}

// Toutes les FAQs disponibles
const allFaqs = ref<FaqItem[]>(question_responses)

// FAQs actuellement affichées
const displayedFaqs = ref<FaqItem[]>([])

// État de chargement
const isLoading = ref(false)

// IDs des FAQs déjà vues (historique)
const viewedFaqIds = ref<Set<number>>(new Set())

// Initialiser avec 4 FAQs aléatoires
const initializeFaqs = () => {
  const shuffled = shuffleArray(allFaqs.value)
  const initialFaqs = shuffled.slice(0, 4)
  displayedFaqs.value = initialFaqs
  initialFaqs.forEach(faq => viewedFaqIds.value.add(faq.id))
}

// Vérifier s'il reste des FAQs non vues
const hasMoreFaqs = computed(() => {
  return viewedFaqIds.value.size < allFaqs.value.length
})

// Fonction pour mélanger un tableau (Fisher-Yates shuffle)
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// Charger 4 nouvelles FAQs aléatoires (remplacer les existantes)
const loadMoreFaqs = () => {
  if (isLoading.value) return
  
  isLoading.value = true
  
  // Simuler un délai de chargement pour l'effet
  setTimeout(() => {
    // Filtrer les FAQs non encore vues
    const remainingFaqs = allFaqs.value.filter(
      faq => !viewedFaqIds.value.has(faq.id)
    )
    
    // Si plus de FAQs non vues, réinitialiser l'historique (boucle infinie)
    if (remainingFaqs.length === 0) {
      viewedFaqIds.value.clear()
      const shuffled = shuffleArray(allFaqs.value)
      const newFaqs = shuffled.slice(0, 4)
      displayedFaqs.value = newFaqs
      newFaqs.forEach(faq => viewedFaqIds.value.add(faq.id))
    } else {
      // Mélanger aléatoirement
      const shuffled = shuffleArray(remainingFaqs)
      
      // Prendre 4 FAQs (ou moins s'il en reste moins)
      const newFaqs = shuffled.slice(0, Math.min(4, shuffled.length))
      
      // REMPLACER les FAQs affichées (ne pas ajouter)
      displayedFaqs.value = newFaqs
      
      // Mettre à jour les IDs vus
      newFaqs.forEach(faq => viewedFaqIds.value.add(faq.id))
    }
    
    // Réinitialiser openItems (toutes les FAQs fermées)
    openItems.value = new Array(4).fill(false)
    
    isLoading.value = false
  }, 600)
}

// State management for accordion items
const openItems = ref<boolean[]>([])

// Toggle item - ferme toutes les autres quand on en ouvre une
const toggleItem = (index: number) => {
  // Si on ouvre un item, fermer tous les autres
  if (!openItems.value[index]) {
    openItems.value = openItems.value.map(() => false)
  }
  // Toggle l'item cliqué
  openItems.value[index] = !openItems.value[index]
}

// Initialiser au montage
initializeFaqs()
openItems.value = new Array(4).fill(false)
</script>

<style scoped>
/* Transition pour le remplacement des FAQs */
.fade-move,
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.fade-leave-active {
  position: absolute;
}
</style>